import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useFlag, useFlags, usePass, usePassOrder } from '../api/hooks';
import { PayMethods, useStarsOrder } from '../components/pass/payments';
import { ErrorView, Loader } from '../components/StateViews';
import { BackIcon } from '../components/icons';
import { seasonTimer } from '../components/pass/level';
import { OrderSheet } from './DecksScreen';
import { CosmeticAvatar } from '../components/cosmetics/CosmeticAvatar';
import { useAuth } from '../auth/AuthProvider';
import { backButton, canPayStars, haptics } from '../telegram';
import { track } from '../analytics';

/** 6.2a — что входит в Straddle Pass+; «Получить» создаёт заказ (код+QR),
 *  оплату отмечает админ на стойке в CRM. */
export function PassPlusScreen() {
  const flags = useFlags();
  const enabled = Boolean(flags.data?.pass);
  const pass = usePass(enabled);
  const order = usePassOrder();
  const { player } = useAuth();
  const navigate = useNavigate();
  const [orderOpen, setOrderOpen] = useState(false);
  const starsFlag = useFlag('pass_stars');
  // После инвойса (или если висит desk-заказ) — открыть лист заказа;
  // оплаченный Pass+ подхватится сам (tier → редирект на дорожку).
  const stars = useStarsOrder(() => setOrderOpen(true));

  useEffect(() => {
    const back = () => navigate(-1);
    backButton.show(back);
    return () => backButton.hide(back);
  }, [navigate]);

  if (flags.data && !enabled) return <Navigate to="/" replace />;
  if (!flags.data) return <Loader />;
  if (pass.isLoading) return <Loader />;
  if (pass.isError || !pass.data?.season || !pass.data.me) {
    return <ErrorView message={(pass.error as Error)?.message} />;
  }
  const { season, me } = pass.data;
  if (me.tier === 'plus') return <Navigate to="/pass" replace />;

  const passedRewards = (pass.data.tracks ?? []).filter(
    (t) => t.level <= me.level && t.plus,
  ).length;

  const starsPrice =
    starsFlag && canPayStars && season.pricePlusStars != null ? season.pricePlusStars : null;
  const busy = order.isPending || stars.pending;

  const buy = () => {
    haptics.tap();
    track('pass_plus_order', { level: me.level });
    void order
      .mutateAsync({ kind: 'plus' })
      .then(() => setOrderOpen(true))
      .catch(() => haptics.error());
  };

  return (
    <div className="bp-screen">
      <div className="subhead">
        <button className="iconbtn" onClick={() => navigate(-1)} aria-label="Назад">
          <BackIcon />
        </button>
        <div className="title">Straddle Pass+</div>
      </div>

      <div className="bp-plus-hero" data-theme="dark">
        <div className="bp-plus-hero-top">
          <div className="bp-hero-label">STRADDLE PASS+ · {season.name.toUpperCase()}</div>
          <div className="bp-plus-days mono">{seasonTimer(season.endsAt)}</div>
        </div>
        <div className="bp-plus-ava">
          {/* Аватарка в «Золотой короне» (спека 6.2a) */}
          <CosmeticAvatar
            name={player?.displayName}
            src={player?.photoUrl}
            size={44}
            frame={{ code: 'frame_gold', kind: 'lottie', asset: 'frames/frame_gold.lottie', rarity: 3 }}
          />
          <div>
            <div className="bp-plus-title">Награда на каждом уровне</div>
            {/* макет: цена крупно моно золотом + подпись по базовой линии */}
            <div className="bp-plus-price">
              <b className="mono">{season.pricePlus.toLocaleString('ru-RU')} ₽</b>
              <span>на весь сезон</span>
            </div>
          </div>
        </div>
        {passedRewards > 0 && (
          <div className="bp-plus-retro">
            <b className="mono">+{passedRewards}</b> наград за уже пройденные уровни откроются сразу
          </div>
        )}
      </div>

      <div className="bp-plus-card">
        <div className="bp-plus-cardhead">
          <span>Ваучеры на игры</span>
          <span className="gt">4 за сезон</span>
        </div>
        {[
          ['Бесплатный вход', '1 вход', 'ур. 10'],
          ['Бесплатный ре-энтри', 'RE ×2', 'ур. 15 · 27'],
          ['Напиток за 1 ₽', '1 ₽', 'ур. 5'],
        ].map(([nm, tag, lvl]) => (
          <div key={nm} className="bp-plus-voucher">
            {/* билетик 44×26 с вырезами (макет) */}
            <span className="tk mono">{tag}</span>
            <span className="nm">{nm}</span>
            <span className="lvl mono">{lvl}</span>
          </div>
        ))}
      </div>

      <div className="bp-plus-card">
        {[
          ['30 наград вместо 10', '15 предметов косметики, в том числе легендарные', '#B8A7D6', 'M4 20h16M6 20V9l4 3 2-6 2 6 4-3v11'],
          ['Колоды большей редкости', '11 колод на дорожке, две легендарные', '#9FB4D8', 'M7 3h10a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2zM9 9h6'],
          ['Заморозки баллов отбора', 'Защита баллов при пропуске игр, только с Pass+', '#7FD3F0', 'M12 3v18M4.2 7.5l15.6 9M4.2 16.5l15.6-9'],
        ].map(([nm, sub, c, path]) => (
          <div key={nm} className="bp-plus-point">
            <span className="ic" style={{ color: c, borderColor: c }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d={path} />
              </svg>
            </span>
            <span className="tx">
              <span className="nm">{nm}</span>
              <span className="sub">{sub}</span>
            </span>
          </div>
        ))}
      </div>

      <div className="bp-bottom">
        {/* Единый выбор способа оплаты: ⭐ (если включены) + администратор;
            новые способы добавляются в массив methods. */}
        <PayMethods
          methods={[
            ...(starsPrice !== null
              ? [
                  {
                    key: 'stars',
                    label: `Оплатить ${starsPrice.toLocaleString('ru-RU')} ⭐ в Telegram`,
                    primary: true,
                    disabled: busy,
                    onPick: () => void stars.buy({ kind: 'plus' }).catch(() => haptics.error()),
                  },
                ]
              : []),
            {
              key: 'desk',
              label: `Оплата у администратора — ${season.pricePlus.toLocaleString('ru-RU')} ₽`,
              primary: starsPrice === null,
              disabled: busy,
              onPick: buy,
            },
          ]}
        />
        <div className="bp-bottom-note mono">
          {starsPrice !== null
            ? 'Звёзды спишутся в Telegram · у администратора — по коду заказа'
            : 'Оплата у администратора клуба по коду заказа'}
        </div>
      </div>

      {orderOpen && <OrderSheet onClose={() => setOrderOpen(false)} />}
    </div>
  );
}
